import React, { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { ShoppingBag, User, Menu } from 'lucide-react';
import { useSite } from '../context/SiteContext';
import { useCart } from '../context/CartContext';
import { useCustomerAuth } from '../context/CustomerAuthContext';
import { CurrencySwitcher } from './CurrencySwitcher';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';

export default function Header() {
    const { settings, navigation } = useSite();
    const { count, setDrawerOpen } = useCart();
    const { customer } = useCustomerAuth();
    const [scrolled, setScrolled] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);
    const location = useLocation();
    const headerRef = useRef(null);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 16);
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    // expose header height as a CSS var so sticky toolbars can sit right below it
    useEffect(() => {
        const el = headerRef.current;
        if (!el) return;
        const setVar = () => document.documentElement.style.setProperty('--header-h', `${el.offsetHeight}px`);
        setVar();
        const ro = new ResizeObserver(setVar);
        ro.observe(el);
        window.addEventListener('resize', setVar);
        return () => { ro.disconnect(); window.removeEventListener('resize', setVar); };
    }, []);

    useEffect(() => { setMobileOpen(false); }, [location.pathname]);

    const left = navigation?.left || [];
    const right = navigation?.right || [];

    return (
        <header
            ref={headerRef}
            data-testid="public-header"
            className="sticky top-0 z-40"
            style={{
                backgroundColor: 'rgba(254, 242, 245, 0.92)',
                backdropFilter: 'blur(10px)',
                borderBottom: scrolled ? '1px solid rgba(94,33,52,0.12)' : '1px solid transparent',
                transition: 'border-color 220ms ease, padding 220ms ease',
            }}
        >
            <div className="max-w-[1300px] mx-auto px-4 sm:px-6 lg:px-10">
                {/* Desktop */}
                <div
                    className="hidden md:grid items-center gap-6"
                    style={{
                        gridTemplateColumns: '1fr auto 1fr auto',
                        paddingTop: scrolled ? '12px' : '16px',
                        paddingBottom: scrolled ? '12px' : '16px',
                        transition: 'padding 220ms ease',
                    }}
                >
                    <nav className="flex items-center gap-7 justify-start">
                        {left.map((item) => (
                            <NavLink
                                key={item.href + item.label}
                                to={item.href}
                                data-testid={`nav-left-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                                className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
                            >
                                {item.label}
                            </NavLink>
                        ))}
                    </nav>
                    <Link to="/" className="flex items-center justify-center" data-testid="header-logo-link">
                        {settings?.logo_url ? (
                            <img
                                src={settings.logo_url}
                                alt={settings.brand_name || 'by anfal'}
                                className="object-contain w-auto"
                                style={{ height: scrolled ? '64px' : '96px', transition: 'height 220ms ease' }}
                            />
                        ) : (
                            <span className="font-serif text-2xl" style={{ color: 'var(--hf-text)' }}>by anfal</span>
                        )}
                    </Link>
                    <nav className="flex items-center gap-7 justify-end">
                        {right.map((item) => (
                            <NavLink
                                key={item.href + item.label}
                                to={item.href}
                                data-testid={`nav-right-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                                className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
                            >
                                {item.label}
                            </NavLink>
                        ))}
                    </nav>
                    <div className="flex items-center gap-4 justify-end">
                        <CurrencySwitcher variant="light" className="hidden lg:inline-flex" />
                        <Link to="/account" data-testid="header-account-button" aria-label="Account" className="p-2 rounded-full hover:bg-white/60 transition-colors">
                            {customer ? (
                                <span className="flex items-center justify-center w-6 h-6 rounded-full text-[11px] font-medium text-white" style={{ backgroundColor: 'var(--hf-text)' }}>
                                    {(customer.name || customer.email).charAt(0).toUpperCase()}
                                </span>
                            ) : (
                                <User className="w-5 h-5" style={{ color: 'var(--hf-text)' }} />
                            )}
                        </Link>
                        <button
                            onClick={() => setDrawerOpen(true)}
                            data-testid="header-bag-button"
                            aria-label="Open bag"
                            className="relative p-2 rounded-full hover:bg-white/60 transition-colors"
                        >
                            <ShoppingBag className="w-5 h-5" style={{ color: 'var(--hf-text)' }} />
                            {count > 0 && (
                                <span
                                    data-testid="header-bag-count"
                                    className="absolute -top-0.5 -right-0.5 text-[10px] font-medium text-white rounded-full px-1.5 min-w-[18px] h-[18px] flex items-center justify-center"
                                    style={{ backgroundColor: 'var(--hf-text)' }}
                                >
                                    {count}
                                </span>
                            )}
                        </button>
                    </div>
                </div>

                {/* Mobile */}
                <div className="md:hidden flex items-center justify-between py-3">
                    <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                        <SheetTrigger asChild>
                            <button data-testid="header-menu-button" aria-label="Open menu" className="p-2">
                                <Menu className="w-5 h-5" style={{ color: 'var(--hf-text)' }} />
                            </button>
                        </SheetTrigger>
                        <SheetContent side="left" className="bg-[var(--hf-bg)] border-r border-[rgba(94,33,52,0.12)] w-72">
                            <div className="py-2">
                                <Link to="/" className="block mb-8">
                                    {settings?.logo_url && (
                                        <img src={settings.logo_url} alt={settings.brand_name} className="h-16 w-auto object-contain" />
                                    )}
                                </Link>
                                <div className="flex flex-col gap-5">
                                    {[...left, ...right].map((item) => (
                                        <NavLink key={item.href + item.label} to={item.href} className="nav-link">
                                            {item.label}
                                        </NavLink>
                                    ))}
                                    <NavLink to="/account" className="nav-link" data-testid="mobile-nav-account">{customer ? 'My Account' : 'Sign In / Register'}</NavLink>
                                    <NavLink to="/admin/login" className="nav-link">Admin</NavLink>
                                    <div className="pt-2"><CurrencySwitcher variant="light" /></div>
                                </div>
                            </div>
                        </SheetContent>
                    </Sheet>
                    <Link to="/" className="flex items-center">
                        {settings?.logo_url && (
                            <img src={settings.logo_url} alt={settings.brand_name} className="h-16 w-auto object-contain" />
                        )}
                    </Link>
                    <button
                        onClick={() => setDrawerOpen(true)}
                        data-testid="header-bag-button-mobile"
                        className="relative p-2"
                    >
                        <ShoppingBag className="w-5 h-5" style={{ color: 'var(--hf-text)' }} />
                        {count > 0 && (
                            <span className="absolute -top-0.5 -right-0.5 text-[10px] text-white rounded-full px-1.5 min-w-[18px] h-[18px] flex items-center justify-center" style={{ backgroundColor: 'var(--hf-text)' }}>
                                {count}
                            </span>
                        )}
                    </button>
                </div>
            </div>
        </header>
    );
}
